import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { authApi, setToken, getToken, getDashboardPathForUser } from '../lib/api';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 800;

/**
 * Échange du jeton à usage unique (Google OAuth) contre la session. Aucun message affiché, redirection immédiate.
 * Lit le jeton depuis le chemin (/auth/complete/TOKEN) ou la query (?t=) pour fiabilité mobile.
 */
const AuthComplete: React.FC = () => {
  const { t: tParam } = useParams<{ t: string }>();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  const t = tParam || searchParams.get('t');
  const to = searchParams.get('to') || '/dashboard';

  useEffect(() => {
    if (!t) {
      setError('Lien invalide.');
      return;
    }

    let cancelled = false;

    const run = (attempt: number) => {
      authApi
        .completeOAuth(t)
        .then(async (data) => {
          if (cancelled) return;
          const savedToken = data.token;
          setToken(savedToken);
          const returnTo = data.return_to && data.return_to.startsWith('/') ? data.return_to : to;
          const isOnboardingPath = returnTo.includes('/onboarding/plan');
          try {
            const u = await authApi.getUser();
            if (cancelled) return;
            if (u) {
              if (isOnboardingPath) {
                window.location.replace(returnTo);
                return;
              }
              window.location.replace(getDashboardPathForUser(u, window.location.pathname));
              return;
            }
          } catch {
            /* fall through */
          }
          if (!getToken() && savedToken) setToken(savedToken);
          window.location.replace(returnTo);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < MAX_RETRIES) {
            setTimeout(() => run(attempt + 1), RETRY_DELAY_MS);
          } else {
            setError('Connexion échouée.');
          }
        });
    };

    run(0);
    return () => { cancelled = true; };
  }, [t, to]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <a href="/signin" className="text-[#3C59B2] underline">Retour à la connexion</a>
        </div>
      </div>
    );
  }

  return <div className="min-h-screen bg-white" aria-hidden="true" />;
};

export default AuthComplete;
